import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FileText, Users, Settings, DollarSign, Plus, Edit, Trash2, Download, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || '';

function Preventivi({ user }) {
  // TEST MODE: Render semplice per debug
  if (!user) {
    return <div className="p-4 bg-red-100">ERROR: No user prop</div>;
  }
  // State principale
  const [activeSubTab, setActiveSubTab] = useState('lista');
  const [preventivi, setPreventivi] = useState([]);
  const [prestazioni, setPrestazioni] = useState([]);
  const [configurazione, setConfigurazione] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // State per filtri
  const [filtroAnno, setFiltroAnno] = useState(new Date().getFullYear());
  const [filtroMese, setFiltroMese] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  
  // State per clienti
  const [clienti, setClienti] = useState([]);
  
  // State per creazione/modifica preventivo
  const [showPreventivoDialog, setShowPreventivoDialog] = useState(false);
  const [preventivoCorrente, setPreventivoCorrente] = useState(null);
  const [clienteData, setClienteData] = useState({
    nome: '',
    cf_piva: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    telefono: '',
    email: ''
  });
  const [righe, setRighe] = useState([]);
  const [scontoTipo, setScontoTipo] = useState(null);
  const [scontoValore, setScontoValore] = useState(0);
  const [note, setNote] = useState('');
  const [condizioni, setCondizioni] = useState('');
  
  // State per configurazione (Solo Super Admin)
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configData, setConfigData] = useState({
    nome_azienda: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    partita_iva: '',
    codice_fiscale: '',
    telefono: '',
    email: '',
    iban: '',
    nome_banca: '',
    costo_km: 0,
    iva_default: 22,
    validita_preventivo_giorni: 30
  });
  
  // State per catalogo prestazioni (Solo Super Admin)
  const [showPrestazioneDialog, setShowPrestazioneDialog] = useState(false);
  const [prestazioneCorrente, setPrestazioneCorrente] = useState(null);
  const [prestazioneData, setPrestazioneData] = useState({
    nome: '',
    descrizione: '',
    categoria: 'servizio_sanitario',
    prezzo_unitario: 0,
    iva_percentuale: 22,
    unita_misura: 'servizio'
  });
  
  // State per categorie
  const [categorie, setCategorie] = useState([]);
  const [showCategoriaDialog, setShowCategoriaDialog] = useState(false);
  const [categoriaData, setCategoriaData] = useState({
    nome: '',
    descrizione: ''
  });
  
  // State per calcolo distanze
  const [showCalcoloDistanza, setShowCalcoloDistanza] = useState(false);
  const [distanzaData, setDistanzaData] = useState({
    origine: '',
    destinazione: '',
    andata_ritorno: false
  });
  
  const isSuperAdmin = user.role === 'super_amministratore';
  
  const fetchPreventivi = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/preventivi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreventivi(response.data);
    } catch (error) {
      alert('Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch configurazione
  const fetchConfigurazione = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/preventivi/configurazione`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setConfigurazione(response.data);
        setConfigData(response.data);
      }
    } catch (error) {
      console.error('Errore caricamento configurazione:', error);
    }
  }, []);
  
  // Fetch prestazioni
  const fetchPrestazioni = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/preventivi/prestazioni`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrestazioni(response.data);
    } catch (error) {
      console.error('Errore caricamento prestazioni:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch categorie
  const fetchCategorie = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/preventivi/categorie`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategorie(response.data);
    } catch (error) {
      console.error('Errore caricamento categorie:', error);
    }
  }, []);

  useEffect(() => {
    if (activeSubTab === 'lista') {
      fetchPreventivi();
    } else if (activeSubTab === 'configurazione' && isSuperAdmin) {
      fetchConfigurazione();
    } else if (activeSubTab === 'catalogo' && isSuperAdmin) {
      fetchPrestazioni();
      fetchCategorie();
    }
  }, [activeSubTab, isSuperAdmin, fetchPreventivi, fetchConfigurazione, fetchPrestazioni, fetchCategorie]);
  
  useEffect(() => {
    if (showPreventivoDialog) {
      fetchPrestazioni();
      fetchConfigurazione();
      fetchCategorie();
    }
  }, [showPreventivoDialog, fetchPrestazioni, fetchConfigurazione, fetchCategorie]);
  
  // Crea categoria
  const creaCategoria = async () => {
    if (!categoriaData.nome) {
      alert('Inserisci il nome della categoria');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/preventivi/categorie`,
        null,
        {
          params: {
            nome: categoriaData.nome,
            descrizione: categoriaData.descrizione
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('Categoria creata con successo!');
      setShowCategoriaDialog(false);
      setCategoriaData({ nome: '', descrizione: '' });
      fetchCategorie();
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore nella creazione della categoria');
    }
  };
  
  // Elimina categoria
  const eliminaCategoria = async (categoriaId) => {
    if (!confirm('Sei sicuro di voler eliminare questa categoria?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/preventivi/categorie/${categoriaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Categoria eliminata!');
      fetchCategorie();
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore nell\'eliminazione della categoria');
    }
  };

  // Elimina prestazione
  const eliminaPrestazione = async (prestazioneId) => {
    if (!confirm('Sei sicuro di voler eliminare questa prestazione?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/preventivi/prestazioni/${prestazioneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Prestazione eliminata con successo!');
      fetchPrestazioni();
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore nell\'eliminazione della prestazione');
    }
  };
  
  // Salva configurazione
  const salvaConfigurazione = async () => {
    // Validazione campi obbligatori
    const campiObbligatori = ['nome_azienda', 'indirizzo', 'citta', 'cap', 'provincia', 'partita_iva', 'telefono', 'email', 'iban', 'nome_banca'];
    const campiMancanti = campiObbligatori.filter(campo => !configData[campo] || configData[campo].toString().trim() === '');
    
    if (campiMancanti.length > 0) {
      alert(`Compila tutti i campi obbligatori: ${campiMancanti.join(', ')}`);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BACKEND_URL}/api/preventivi/configurazione`, configData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Configurazione salvata con successo!');
      await fetchConfigurazione();
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore nel salvataggio della configurazione');
    }
  };
  
  // Salva/Aggiorna prestazione
  const salvaPrestazione = async () => {
    // Validazione campi
    if (!prestazioneData.nome || !prestazioneData.nome.trim()) {
      alert('Inserisci il nome della prestazione');
      return;
    }
    
    if (!prestazioneData.prezzo_unitario || prestazioneData.prezzo_unitario <= 0) {
      alert('Inserisci un prezzo valido');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (prestazioneCorrente) {
        // Update
        await axios.put(`${BACKEND_URL}/api/preventivi/prestazioni/${prestazioneCorrente.id}`, prestazioneData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Prestazione aggiornata con successo!');
      } else {
        // Create
        const response = await axios.post(`${BACKEND_URL}/api/preventivi/prestazioni`, prestazioneData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Prestazione creata con successo!');
      }
      setShowPrestazioneDialog(false);
      // Reset form
      setPrestazioneData({
        nome: '',
        descrizione: '',
        categoria: categorie.length > 0 ? categorie[0].id : 'servizio_sanitario',
        prezzo_unitario: 0,
        iva_percentuale: 22,
        unita_misura: 'servizio'
      });
      // Ricarica prestazioni
      await fetchPrestazioni();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore nel salvataggio della prestazione';
      alert('❌ ' + errorMsg);
    }
  };
  
  // Calcola distanza con Google Maps
  const calcolaDistanza = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/preventivi/calcola-distanza`, distanzaData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { distanza_km } = response.data;
      
      // Aggiungi riga trasporto con km calcolati
      const costoKm = configurazione?.costo_km || 0;
      const ivaDefault = configurazione?.iva_default || 22;
      
      const nuovaRiga = {
        descrizione: `Trasporto da ${distanzaData.origine} a ${distanzaData.destinazione}${distanzaData.andata_ritorno ? ' (A/R)' : ''}`,
        quantita: distanza_km,
        prezzo_unitario: costoKm,
        iva_percentuale: ivaDefault
      };
      
      setRighe([...righe, nuovaRiga]);
      setShowCalcoloDistanza(false);
      alert(`Distanza calcolata: ${distanza_km} km`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore nel calcolo della distanza.';
      alert(errorMsg + '\n\nSe non hai configurato la API key, registrati gratuitamente su https://openrouteservice.org e aggiungi OPENROUTESERVICE_API_KEY nel file .env del backend.');
    }
  };
  
  // Crea/Aggiorna preventivo
  const salvaPreventivo = async () => {
    if (!clienteData.nome) {
      alert('Inserisci il nome del cliente');
      return;
    }
    
    if (righe.length === 0) {
      alert('Aggiungi almeno una riga al preventivo');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        cliente: clienteData,
        righe: righe,
        sconto_tipo: scontoTipo,
        sconto_valore: scontoValore,
        note: note,
        condizioni: condizioni
      };
      
      if (preventivoCorrente) {
        // Update
        await axios.put(`${BACKEND_URL}/api/preventivi/${preventivoCorrente.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Preventivo aggiornato!');
      } else {
        // Create
        await axios.post(`${BACKEND_URL}/api/preventivi`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Preventivo creato!');
      }
      
      setShowPreventivoDialog(false);
      resetPreventivoForm();
      fetchPreventivi();
    } catch (error) {
      alert('Errore nel salvataggio del preventivo');
    }
  };
  
  const resetPreventivoForm = () => {
    setPreventivoCorrente(null);
    setClienteData({
      nome: '',
      cf_piva: '',
      indirizzo: '',
      citta: '',
      cap: '',
      provincia: '',
      telefono: '',
      email: ''
    });
    setRighe([]);
    setScontoTipo(null);
    setScontoValore(0);
    setNote('');
    setCondizioni('');
  };

  // Cambia stato preventivo
  const cambiaStatoPreventivo = async (preventivoId, nuovoStato) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/preventivi/${preventivoId}`, {
        stato: nuovoStato
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Stato aggiornato a: ${nuovoStato}`);
      fetchPreventivi(); // Ricarica lista
    } catch (error) {
      alert('Errore nel cambio stato del preventivo');
    }
  };

  
  // Download PDF
  const downloadPDF = async (preventivoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/preventivi/${preventivoId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preventivo_${preventivoId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Errore nel download del PDF. Assicurati che la configurazione aziendale sia completa.');
    }
  };
  
  // Calcola totali in tempo reale
  const calcolaTotali = () => {
    let imponibile = 0;
    let totaleIva = 0;
    
    righe.forEach(riga => {
      const importoLordo = riga.quantita * riga.prezzo_unitario;
      const importoIva = importoLordo * (riga.iva_percentuale / 100);
      imponibile += importoLordo;
      totaleIva += importoIva;
    });
    
    const totale = imponibile + totaleIva;
    let scontoImporto = 0;
    
    if (scontoTipo === 'percentuale' && scontoValore > 0) {
      scontoImporto = totale * (scontoValore / 100);
    } else if (scontoTipo === 'importo' && scontoValore > 0) {
      scontoImporto = scontoValore;
    }
    
    const totaleScontato = totale - scontoImporto;
    
    return {
      imponibile: imponibile.toFixed(2),
      totaleIva: totaleIva.toFixed(2),
      totale: totale.toFixed(2),
      scontoImporto: scontoImporto.toFixed(2),
      totaleScontato: totaleScontato.toFixed(2)
    };
  };
  
  const totali = calcolaTotali();
  
  // Estrai clienti unici dai preventivi
  const estraiClienti = () => {
    const clientiMap = new Map();
    preventivi.forEach(prev => {
      const cliente = prev.cliente;
      const key = cliente.cf_piva || cliente.email || cliente.nome;
      if (!clientiMap.has(key)) {
        clientiMap.set(key, {
          ...cliente,
          preventivi_count: 1,
          ultimo_preventivo: prev.data_emissione
        });
      } else {
        const existing = clientiMap.get(key);
        existing.preventivi_count += 1;
        if (new Date(prev.data_emissione) > new Date(existing.ultimo_preventivo)) {
          existing.ultimo_preventivo = prev.data_emissione;
        }
      }
    });
    setClienti(Array.from(clientiMap.values()));
  };
  
  // Filtra preventivi in base ai filtri
  const preventiviFiltrati = preventivi.filter(prev => {
    const dataEmissione = new Date(prev.data_emissione);
    const anno = dataEmissione.getFullYear();
    const mese = dataEmissione.getMonth() + 1;
    
    if (filtroAnno && anno !== parseInt(filtroAnno)) return false;
    if (filtroMese && mese !== parseInt(filtroMese)) return false;
    if (filtroStato && prev.stato !== filtroStato) return false;
    
    // Filtro per categoria: controlla se almeno una riga ha quella categoria
    if (filtroCategoria) {
      const hasCategoria = prev.righe.some(riga => {
        if (riga.prestazione_id) {
          const prestazione = prestazioni.find(p => p.id === riga.prestazione_id);
          return prestazione && prestazione.categoria === filtroCategoria;
        }
        return false;
      });
      if (!hasCategoria) return false;
    }
    
    return true;
  });
  
  // Genera array di anni disponibili
  const anniDisponibili = [...new Set(preventivi.map(p => new Date(p.data_emissione).getFullYear()))].sort((a, b) => b - a);
  if (anniDisponibili.length === 0) anniDisponibili.push(new Date().getFullYear());

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto pb-px scrollbar-hide">
        {[
          { key: 'lista', label: 'Preventivi', icon: FileText },
          { key: 'clienti', label: 'Clienti', icon: Users, onClick: () => estraiClienti() },
          ...(isSuperAdmin ? [
            { key: 'configurazione', label: 'Configurazione', icon: Settings },
            { key: 'catalogo', label: 'Catalogo', icon: DollarSign }
          ] : [])
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => { setActiveSubTab(tab.key); tab.onClick?.(); }}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeSubTab === tab.key 
                  ? 'text-cyan-700 border-cyan-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* LISTA PREVENTIVI */}
      {activeSubTab === 'lista' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <h2 className="text-lg font-bold text-gray-900">Preventivi</h2>
            <Button
              onClick={() => {
                resetPreventivoForm();
                setShowPreventivoDialog(true);
              }}
              size="sm"
              className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Nuovo Preventivo
            </Button>
          </div>
          
          {/* Filtri */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              <select value={filtroAnno} onChange={(e) => setFiltroAnno(e.target.value)}
                className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700">
                <option value="">Tutti gli anni</option>
                {anniDisponibili.map(anno => (
                  <option key={anno} value={anno}>{anno}</option>
                ))}
              </select>
              <select value={filtroMese} onChange={(e) => setFiltroMese(e.target.value)}
                className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700">
                <option value="">Tutti i mesi</option>
                <option value="1">Gennaio</option><option value="2">Febbraio</option>
                <option value="3">Marzo</option><option value="4">Aprile</option>
                <option value="5">Maggio</option><option value="6">Giugno</option>
                <option value="7">Luglio</option><option value="8">Agosto</option>
                <option value="9">Settembre</option><option value="10">Ottobre</option>
                <option value="11">Novembre</option><option value="12">Dicembre</option>
              </select>
              <select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}
                className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700">
                <option value="">Tutte le categorie</option>
                <option value="servizio_sanitario">Servizio Sanitario</option>
                <option value="trasporto_sanitario">Trasporto Sanitario</option>
                <option value="altro">Altro</option>
              </select>
              <select value={filtroStato} onChange={(e) => setFiltroStato(e.target.value)}
                className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700">
                <option value="">Tutti gli stati</option>
                <option value="bozza">Bozza</option><option value="inviato">Inviato</option>
                <option value="accettato">Accettato</option><option value="rifiutato">Rifiutato</option>
                <option value="scaduto">Scaduto</option>
              </select>
              {(filtroAnno || filtroMese || filtroCategoria || filtroStato) && (
                <button onClick={() => { setFiltroAnno(new Date().getFullYear()); setFiltroMese(''); setFiltroCategoria(''); setFiltroStato(''); }}
                  className="h-8 px-2 text-xs text-gray-500 hover:text-gray-700 underline">
                  Azzera
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400">
              {preventiviFiltrati.length} di {preventivi.length} preventivi
            </p>
          </div>
          
          {loading ? (
            <p>Caricamento...</p>
          ) : preventiviFiltrati.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                {preventivi.length === 0 
                  ? "Nessun preventivo presente. Crea il primo preventivo!" 
                  : "Nessun preventivo corrisponde ai filtri selezionati."}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {preventiviFiltrati.map(prev => (
                <Card key={prev.id} className="border border-gray-200 hover:shadow-sm transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-gray-900">{prev.numero}</h3>
                          <p className="text-xs text-gray-500">Cliente: {prev.cliente.nome}</p>
                        </div>
                        <select
                          value={prev.stato}
                          onChange={(e) => cambiaStatoPreventivo(prev.id, e.target.value)}
                          className={`px-2 py-1 rounded-md text-[11px] font-medium border cursor-pointer ${
                            prev.stato === 'bozza' ? 'bg-gray-50 border-gray-200 text-gray-600' :
                            prev.stato === 'inviato' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                            prev.stato === 'accettato' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                            prev.stato === 'rifiutato' ? 'bg-red-50 border-red-200 text-red-600' :
                            'bg-gray-50 border-gray-200 text-gray-600'
                          }`}
                        >
                          <option value="bozza">BOZZA</option>
                          <option value="inviato">INVIATO</option>
                          <option value="accettato">ACCETTATO</option>
                          <option value="rifiutato">RIFIUTATO</option>
                          <option value="scaduto">SCADUTO</option>
                        </select>
                      </div>
                      
                      <div className="flex items-center gap-3 pt-1">
                        <div className="text-xs text-gray-400">
                          {new Date(prev.data_emissione).toLocaleDateString('it-IT')} — Scad: {new Date(prev.data_scadenza).toLocaleDateString('it-IT')}
                        </div>
                        <span className="text-sm font-bold text-gray-900 ml-auto">
                          € {prev.totale_scontato > 0 ? prev.totale_scontato.toFixed(2) : prev.totale.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex gap-1.5 pt-1">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => {
                            setPreventivoCorrente(prev);
                            setClienteData(prev.cliente);
                            setRighe(prev.righe);
                            setScontoTipo(prev.sconto_tipo || 'percentuale');
                            setScontoValore(prev.sconto_valore || 0);
                            setNote(prev.note || '');
                            setCondizioni(prev.condizioni || '');
                            setShowPreventivoDialog(true);
                          }}
                          className="h-8 gap-1.5 text-xs text-gray-600 border-gray-200 hover:bg-gray-50"
                        >
                          <Edit className="w-3 h-3" />
                          Modifica
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => downloadPDF(prev.id)}
                          className="h-8 gap-1.5 text-xs text-gray-600 border-gray-200 hover:bg-gray-50"
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CLIENTI */}
      {activeSubTab === 'clienti' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Clienti Memorizzati</h2>
            <p className="text-sm text-gray-600">{clienti.length} clienti</p>
          </div>
          
          {clienti.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                Nessun cliente trovato. I clienti vengono estratti automaticamente dai preventivi creati.
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {clienti.map((cliente, index) => (
                <Card key={cliente.id || cliente.nome || index} className="border border-gray-200 hover:shadow-sm transition-shadow">
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm text-gray-900">{cliente.nome}</h3>
                        {cliente.cf_piva && (
                          <p className="text-sm text-gray-600">
                            <strong>CF/P.IVA:</strong> {cliente.cf_piva}
                          </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm text-gray-700">
                          {cliente.indirizzo && (
                            <p>
                              <strong>Indirizzo:</strong> {cliente.indirizzo}
                              {cliente.citta && `, ${cliente.cap} ${cliente.citta} (${cliente.provincia})`}
                            </p>
                          )}
                          {cliente.telefono && (
                            <p><strong>Tel:</strong> {cliente.telefono}</p>
                          )}
                          {cliente.email && (
                            <p><strong>Email:</strong> {cliente.email}</p>
                          )}
                        </div>
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex gap-4 text-xs text-gray-600">
                            <span>
                              <strong>{cliente.preventivi_count}</strong> preventiv{cliente.preventivi_count === 1 ? 'o' : 'i'}
                            </span>
                            <span>
                              Ultimo: <strong>{new Date(cliente.ultimo_preventivo).toLocaleDateString('it-IT')}</strong>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => {
                            // Pre-compila il form con i dati cliente
                            resetPreventivoForm();
                            setClienteData({
                              nome: cliente.nome,
                              cf_piva: cliente.cf_piva || '',
                              indirizzo: cliente.indirizzo || '',
                              citta: cliente.citta || '',
                              cap: cliente.cap || '',
                              provincia: cliente.provincia || '',
                              telefono: cliente.telefono || '',
                              email: cliente.email || ''
                            });
                            setShowPreventivoDialog(true);
                          }}
                          className="px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded text-sm font-medium"
                        >
                          + Nuovo Preventivo
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CONFIGURAZIONE (Solo Super Admin) */}
      {activeSubTab === 'configurazione' && isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Configurazione Dati Aziendali</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome Azienda *</label>
                <input
                  type="text"
                  value={configData.nome_azienda}
                  onChange={(e) => setConfigData({...configData, nome_azienda: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Partita IVA *</label>
                <input
                  type="text"
                  value={configData.partita_iva}
                  onChange={(e) => setConfigData({...configData, partita_iva: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Indirizzo *</label>
                <input
                  type="text"
                  value={configData.indirizzo}
                  onChange={(e) => setConfigData({...configData, indirizzo: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Città *</label>
                <input
                  type="text"
                  value={configData.citta}
                  onChange={(e) => setConfigData({...configData, citta: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CAP *</label>
                <input
                  type="text"
                  value={configData.cap}
                  onChange={(e) => setConfigData({...configData, cap: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Provincia *</label>
                <input
                  type="text"
                  value={configData.provincia}
                  onChange={(e) => setConfigData({...configData, provincia: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  maxLength={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Telefono *</label>
                <input
                  type="text"
                  value={configData.telefono}
                  onChange={(e) => setConfigData({...configData, telefono: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={configData.email}
                  onChange={(e) => setConfigData({...configData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">IBAN *</label>
                <input
                  type="text"
                  value={configData.iban}
                  onChange={(e) => setConfigData({...configData, iban: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nome Banca *</label>
                <input
                  type="text"
                  value={configData.nome_banca}
                  onChange={(e) => setConfigData({...configData, nome_banca: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Costo per KM (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={configData.costo_km}
                  onChange={(e) => setConfigData({...configData, costo_km: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">IVA Default (%)</label>
                <input
                  type="number"
                  value={configData.iva_default}
                  onChange={(e) => setConfigData({...configData, iva_default: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            </div>
            <button
              onClick={salvaConfigurazione}
              className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded hover:bg-cyan-50 font-medium"
            >
              Salva Configurazione
            </button>
          </CardContent>
        </Card>
      )}

      {/* CATALOGO TARIFFE (Solo Super Admin) */}
      {activeSubTab === 'catalogo' && isSuperAdmin && (
        <div className="space-y-4">
          <div className="space-y-3">
            <h2 className="text-xl font-bold">Catalogo Tariffe e Categorie</h2>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setCategoriaData({ nome: '', descrizione: '' });
                  setShowCategoriaDialog(true);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
              >
                + Nuova Categoria
              </button>
              <button
                onClick={() => {
                  setPrestazioneCorrente(null);
                  setPrestazioneData({
                    nome: '',
                    descrizione: '',
                    categoria: categorie.length > 0 ? categorie[0].id : 'servizio_sanitario',
                    prezzo_unitario: 0,
                    iva_percentuale: 22,
                    unita_misura: 'servizio'
                  });
                  setShowPrestazioneDialog(true);
                }}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded hover:bg-cyan-50 font-medium"
              >
                + Nuova Prestazione
              </button>
            </div>
          </div>
          
          {/* Sezione Categorie */}
          <Card className="bg-purple-50">
            <CardHeader>
              <CardTitle className="text-lg">Categorie Prestazioni</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {categorie.map(cat => (
                  <div key={cat.id} className={`p-3 rounded border ${cat.is_default ? 'bg-gray-100 border-gray-300' : 'bg-white border-purple-200'}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{cat.nome}</h4>
                        {cat.descrizione && <p className="text-xs text-gray-600">{cat.descrizione}</p>}
                        {cat.is_default && <span className="text-xs text-gray-500">Default</span>}
                      </div>
                      {!cat.is_default && (
                        <button
                          onClick={() => eliminaCategoria(cat.id)}
                          className="text-red-600 hover:text-red-800 text-xs ml-2"
                          title="Elimina categoria"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {loading ? (
            <p>Caricamento...</p>
          ) : prestazioni.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                Nessuna prestazione nel catalogo. Crea la prima!
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {prestazioni.map(prest => (
                <Card key={prest.id} className={`border ${!prest.attivo ? 'opacity-50' : ''}`}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold">{prest.nome}</h4>
                        <p className="text-xs text-gray-600">{prest.descrizione}</p>
                        <p className="text-sm mt-1">
                          <span className="font-medium">€ {prest.prezzo_unitario.toFixed(2)}</span> / {prest.unita_misura}
                          <span className="ml-2 text-xs">IVA {prest.iva_percentuale}%</span>
                        </p>
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded">{prest.categoria}</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setPrestazioneCorrente(prest);
                            setPrestazioneData(prest);
                            setShowPrestazioneDialog(true);
                          }}
                          className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded text-xs font-medium"
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => eliminaPrestazione(prest.id)}
                          className="px-3 py-1 bg-red-50 text-red-600 border border-red-200 rounded text-xs hover:bg-red-100 font-medium"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialog Nuovo/Modifica Preventivo */}
      {showPreventivoDialog && (
        <Dialog open={showPreventivoDialog} onOpenChange={setShowPreventivoDialog}>
          <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl">{preventivoCorrente ? 'Modifica Preventivo' : 'Nuovo Preventivo'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Dati Cliente */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-bold mb-3">Dati Cliente</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Nome/Ragione Sociale *"
                    value={clienteData.nome}
                    onChange={(e) => setClienteData({...clienteData, nome: e.target.value})}
                    className="px-3 py-2 border rounded"
                  />
                  <input
                    type="text"
                    placeholder="CF/P.IVA"
                    value={clienteData.cf_piva}
                    onChange={(e) => setClienteData({...clienteData, cf_piva: e.target.value})}
                    className="px-3 py-2 border rounded"
                  />
                  <input
                    type="text"
                    placeholder="Indirizzo"
                    value={clienteData.indirizzo}
                    onChange={(e) => setClienteData({...clienteData, indirizzo: e.target.value})}
                    className="px-3 py-2 border rounded"
                  />
                  <input
                    type="text"
                    placeholder="Città"
                    value={clienteData.citta}
                    onChange={(e) => setClienteData({...clienteData, citta: e.target.value})}
                    className="px-3 py-2 border rounded"
                  />
                  <input
                    type="text"
                    placeholder="CAP"
                    value={clienteData.cap}
                    onChange={(e) => setClienteData({...clienteData, cap: e.target.value})}
                    className="px-3 py-2 border rounded"
                  />
                  <input
                    type="text"
                    placeholder="Provincia"
                    value={clienteData.provincia}
                    onChange={(e) => setClienteData({...clienteData, provincia: e.target.value})}
                    className="px-3 py-2 border rounded"
                    maxLength={2}
                  />
                  <input
                    type="tel"
                    placeholder="Telefono"
                    value={clienteData.telefono}
                    onChange={(e) => setClienteData({...clienteData, telefono: e.target.value})}
                    className="px-3 py-2 border rounded"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={clienteData.email}
                    onChange={(e) => setClienteData({...clienteData, email: e.target.value})}
                    className="px-3 py-2 border rounded"
                  />
                </div>
              </div>

              {/* Righe Preventivo */}
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                  <h3 className="font-bold">Righe Preventivo</h3>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => setShowCalcoloDistanza(true)}
                      className="w-full sm:w-auto px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded text-sm font-medium"
                    >
                      🗺️ Calcola Distanza
                    </button>
                    <button
                      onClick={() => {
                        const nuovaRiga = {
                          descrizione: '',
                          quantita: 1,
                          prezzo_unitario: 0,
                          iva_percentuale: configurazione?.iva_default || 22
                        };
                        setRighe([...righe, nuovaRiga]);
                      }}
                      className="w-full sm:w-auto px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                    >
                      + Aggiungi Riga
                    </button>
                  </div>
                </div>

                {righe.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">Nessuna riga. Aggiungi prestazioni o calcola distanze.</p>
                ) : (
                  <div className="overflow-x-auto -mx-2 sm:mx-0">
                    <div className="min-w-[600px] sm:min-w-[700px]">
                      {/* Header righe */}
                      <div className="grid grid-cols-12 gap-1 sm:gap-2 items-center bg-cyan-100 p-2 rounded font-medium text-xs text-cyan-900">
                        <div className="col-span-3 sm:col-span-4">Descrizione</div>
                        <div className="col-span-2 text-center">Qtà</div>
                        <div className="col-span-2 text-center">Prezzo</div>
                        <div className="col-span-2 text-center">IVA</div>
                        <div className="col-span-2 text-center">Totale</div>
                        <div className="col-span-1 text-center">X</div>
                      </div>
                      
                      {/* Righe dati */}
                      <div className="space-y-2 mt-2">
                        {righe.map((riga, index) => (
                          <div key={riga.id || `riga-${index}`} className="grid grid-cols-12 gap-1 sm:gap-2 items-center bg-white p-2 rounded border">
                        <input
                          type="text"
                          placeholder="Descrizione"
                          value={riga.descrizione}
                          onChange={(e) => {
                            const newRighe = [...righe];
                            newRighe[index].descrizione = e.target.value;
                            setRighe(newRighe);
                          }}
                          className="col-span-3 sm:col-span-4 px-1 sm:px-2 py-1 border rounded text-xs sm:text-sm"
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder="1"
                          value={riga.quantita}
                          onChange={(e) => {
                            const newRighe = [...righe];
                            newRighe[index].quantita = parseFloat(e.target.value) || 0;
                            setRighe(newRighe);
                          }}
                          className="col-span-2 px-1 sm:px-2 py-1 border rounded text-xs sm:text-sm"
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder="0"
                          value={riga.prezzo_unitario}
                          onChange={(e) => {
                            const newRighe = [...righe];
                            newRighe[index].prezzo_unitario = parseFloat(e.target.value) || 0;
                            setRighe(newRighe);
                          }}
                          className="col-span-2 px-1 sm:px-2 py-1 border rounded text-xs sm:text-sm"
                        />
                        <input
                          type="number"
                          placeholder="22"
                          value={riga.iva_percentuale}
                          onChange={(e) => {
                            const newRighe = [...righe];
                            newRighe[index].iva_percentuale = parseFloat(e.target.value) || 0;
                            setRighe(newRighe);
                          }}
                          className="col-span-2 px-1 sm:px-2 py-1 border rounded text-xs sm:text-sm"
                        />
                        <span className="col-span-2 text-xs sm:text-sm font-medium text-center">
                          €{(riga.quantita * riga.prezzo_unitario * (1 + riga.iva_percentuale/100)).toFixed(2)}
                        </span>
                        <button
                          onClick={() => {
                            const newRighe = righe.filter((_, i) => i !== index);
                            setRighe(newRighe);
                          }}
                          className="col-span-1 px-1 sm:px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                          title="Elimina riga"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Catalogo Prestazioni Quick Add */}
                {prestazioni.length > 0 && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1">Aggiungi da catalogo:</label>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          const prest = prestazioni.find(p => p.id === e.target.value);
                          if (prest) {
                            const nuovaRiga = {
                              prestazione_id: prest.id,
                              descrizione: prest.nome,
                              quantita: 1,
                              prezzo_unitario: prest.prezzo_unitario,
                              iva_percentuale: prest.iva_percentuale
                            };
                            setRighe([...righe, nuovaRiga]);
                          }
                          e.target.value = '';
                        }
                      }}
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="">Seleziona prestazione...</option>
                      {prestazioni.filter(p => p.attivo).map(prest => (
                        <option key={prest.id} value={prest.id}>
                          {prest.nome} - €{prest.prezzo_unitario} ({prest.categoria})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Sconto */}
              <div className="p-4 bg-yellow-50 rounded-lg">
                <h3 className="font-bold mb-3">Sconto</h3>
                <div className="grid grid-cols-3 gap-3">
                  <select
                    value={scontoTipo || ''}
                    onChange={(e) => setScontoTipo(e.target.value || null)}
                    className="px-3 py-2 border rounded"
                  >
                    <option value="">Nessuno sconto</option>
                    <option value="percentuale">Percentuale</option>
                    <option value="importo">Importo fisso</option>
                  </select>
                  {scontoTipo && (
                    <input
                      type="number"
                      step="0.01"
                      placeholder={scontoTipo === 'percentuale' ? '%' : '€'}
                      value={scontoValore}
                      onChange={(e) => setScontoValore(parseFloat(e.target.value) || 0)}
                      className="px-3 py-2 border rounded"
                    />
                  )}
                </div>
              </div>

              {/* Note e Condizioni */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Note</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Condizioni</label>
                  <textarea
                    value={condizioni}
                    onChange={(e) => setCondizioni(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    rows={3}
                  />
                </div>
              </div>

              {/* Totali */}
              {righe.length > 0 && (
                <div className="p-4 bg-gray-100 rounded-lg">
                  <div className="space-y-1 text-right">
                    <p>Imponibile: <strong>€ {totali.imponibile}</strong></p>
                    <p>IVA: <strong>€ {totali.totaleIva}</strong></p>
                    <p>Totale: <strong>€ {totali.totale}</strong></p>
                    {parseFloat(totali.scontoImporto) > 0 && (
                      <>
                        <p className="text-red-600">Sconto: <strong>- € {totali.scontoImporto}</strong></p>
                        <p className="text-lg font-bold text-cyan-600">TOTALE NETTO: € {totali.totaleScontato}</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={salvaPreventivo}
                className="w-full px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded font-bold"
              >
                {preventivoCorrente ? 'Aggiorna Preventivo' : 'Crea Preventivo'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog Calcolo Distanza */}
      <Dialog open={showCalcoloDistanza} onOpenChange={setShowCalcoloDistanza}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Calcola Distanza Percorso</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Indirizzo Partenza</label>
              <input
                type="text"
                placeholder="Es: Taranto, Via Roma 1"
                value={distanzaData.origine}
                onChange={(e) => setDistanzaData({...distanzaData, origine: e.target.value})}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Indirizzo Destinazione</label>
              <input
                type="text"
                placeholder="Es: Pescara, Piazza Italia"
                value={distanzaData.destinazione}
                onChange={(e) => setDistanzaData({...distanzaData, destinazione: e.target.value})}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={distanzaData.andata_ritorno}
                onChange={(e) => setDistanzaData({...distanzaData, andata_ritorno: e.target.checked})}
                className="w-4 h-4"
              />
              <span>Andata e Ritorno (raddoppia i km)</span>
            </label>
            <p className="text-xs text-gray-500">
              La distanza verrà calcolata automaticamente e aggiunta come riga al preventivo usando il costo/km configurato.
            </p>
            <button
              onClick={calcolaDistanza}
              className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded hover:bg-cyan-50 font-medium"
            >
              Calcola e Aggiungi
            </button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Prestazione */}
      {showPrestazioneDialog && (
        <Dialog open={showPrestazioneDialog} onOpenChange={setShowPrestazioneDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{prestazioneCorrente ? 'Modifica Prestazione' : 'Nuova Prestazione'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Nome Prestazione *</label>
                <input
                  type="text"
                  placeholder="Es: Assistenza Infermieristica"
                  value={prestazioneData.nome}
                  onChange={(e) => setPrestazioneData({...prestazioneData, nome: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione</label>
                <textarea
                  placeholder="Descrizione dettagliata della prestazione"
                  value={prestazioneData.descrizione}
                  onChange={(e) => setPrestazioneData({...prestazioneData, descrizione: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Categoria *</label>
                <select
                  value={prestazioneData.categoria}
                  onChange={(e) => setPrestazioneData({...prestazioneData, categoria: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="servizio_sanitario">Servizio Sanitario</option>
                  <option value="trasporto_sanitario">Trasporto Sanitario</option>
                  <option value="altro">Altro</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Prezzo (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={prestazioneData.prezzo_unitario}
                    onChange={(e) => setPrestazioneData({...prestazioneData, prezzo_unitario: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">IVA (%)</label>
                  <input
                    type="number"
                    placeholder="22"
                    value={prestazioneData.iva_percentuale}
                    onChange={(e) => setPrestazioneData({...prestazioneData, iva_percentuale: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Unità Misura</label>
                  <input
                    type="text"
                    placeholder="servizio/ora/km"
                    value={prestazioneData.unita_misura}
                    onChange={(e) => setPrestazioneData({...prestazioneData, unita_misura: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>
              <button
                onClick={salvaPrestazione}
                className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded hover:bg-cyan-50 font-medium"
              >
                Salva Prestazione
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Dialog Nuova Categoria */}
      {showCategoriaDialog && (
        <Dialog open={showCategoriaDialog} onOpenChange={setShowCategoriaDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuova Categoria Prestazione</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Nome Categoria *</label>
                <input
                  type="text"
                  placeholder="Es: Fisioterapia, Servizi Domiciliari..."
                  value={categoriaData.nome}
                  onChange={(e) => setCategoriaData({...categoriaData, nome: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione</label>
                <textarea
                  placeholder="Descrizione opzionale della categoria"
                  value={categoriaData.descrizione}
                  onChange={(e) => setCategoriaData({...categoriaData, descrizione: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  rows={3}
                />
              </div>
              <p className="text-xs text-gray-500">
                Le categorie ti aiutano a organizzare le prestazioni e filtrare i preventivi.
              </p>
              <button
                onClick={creaCategoria}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Crea Categoria
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default Preventivi;
